export type TierEnum =
  | "free"
  | "pre_sales"
  | "system_tech"
  | "touring"
  | "beta_internal";

export interface TierConfig {
  enum: TierEnum;
  displayName: string;
  tagline: string;
  description: string;
  visibleInSignup: boolean;
  comingSoon?: boolean;
  // Static marketing copy for the signed-out homepage tier cards.
  // Live values (price, included_features, granted_panel_categories) are still
  // sourced from `subscription_plans` via useTierCatalog() on /pricing.
  priceMonthly: number;
  featureLabels: string[];
  panelCategoryLabels: string[];
}

const CATEGORY_LABEL = {
  installation: "Full library of installation panels",
  rental: "Full library of rental panels",
  touring: "Full library of touring panels",
  studio: "Full library of studio panels",
} as const;

export const TIERS: TierConfig[] = [
  {
    enum: "free",
    displayName: "Free",
    tagline: "Try it with your own panel",
    description: "Bring your own panel specs and explore the basics.",
    visibleInSignup: true,
    priceMonthly: 0,
    featureLabels: [
      "Custom Panel",
      "Configuration (your own panel)",
      "Complete Report Tab",
      "Compare Tab",
      "Pitch Compare Tab",
    ],
    panelCategoryLabels: [],
  },
  {
    enum: "pre_sales",
    displayName: "Sales Engineer",
    tagline: "Spec installation systems",
    description: "Built for AV sales engineers specifying installation LED systems.",
    visibleInSignup: true,
    priceMonthly: 10,
    featureLabels: [
      "Saved multi-wall projects",
      "Configuration",
      "Pixel Grid Tab",
      "Complete Report Tab",
      "Custom Panel",
    ],
    panelCategoryLabels: [CATEGORY_LABEL.installation],
  },
  {
    enum: "system_tech",
    displayName: "Technician",
    tagline: "Install, commission, troubleshoot",
    description: "For field technicians, installers, and integrators working with rental LED.",
    visibleInSignup: true,
    priceMonthly: 10,
    featureLabels: [
      "Saved multi-wall projects",
      "Printable crew drawings (data + power)",
      "Configuration",
      "Signal Flow Tab",
      "Data Flow Tab",
      "Power Tab",
      "Technical Drawing Tab",
      "Custom Panel",
    ],
    panelCategoryLabels: [CATEGORY_LABEL.rental],
  },
  {
    enum: "touring",
    displayName: "Touring",
    tagline: "Designed for live events and tours",
    description: "Beta — full-featured access for touring LDs, video engineers, and rental houses. Features are still stabilising.",
    visibleInSignup: true,
    priceMonthly: 10,
    featureLabels: [
      "Saved multi-wall projects",
      "Premium Grid Tab (Customisable)",
      "Complete Report Tab",
      "Truck Pack Tab",
      "Rigging Calculator",
      "Touring Power Flow Tab",
      "Touring Data Flow Tab",
      "Custom Panel",
    ],
    panelCategoryLabels: [CATEGORY_LABEL.touring],
  },
  {
    enum: "beta_internal",
    displayName: "Full Access Testing",
    tagline: "Admin-assigned all-access",
    description: "All-access tier for internal testers. Granted by an administrator — not user-selectable.",

    visibleInSignup: false,
    priceMonthly: 0,
    featureLabels: [],
    panelCategoryLabels: [],
  },
];

export const getVisibleTiers = (): TierConfig[] =>
  TIERS.filter((t) => t.visibleInSignup);

export const getTierByEnum = (enumValue: TierEnum): TierConfig | undefined =>
  TIERS.find((t) => t.enum === enumValue);
